import {
  Col,
  Row,
  Space,
  Table,
  Tag,
  Card,
  Button,
  message,
  Spin,
  Select,
  notification,
  Input,
  Tooltip,
} from "antd";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  ExportOutlined,
  ReloadOutlined,
  FilterOutlined,
  ImportOutlined,
  PlusCircleOutlined,
  UpSquareOutlined,
  DownSquareOutlined,
} from "@ant-design/icons";
import MotionHoc from "../../../utils/MotionHoc";
import {
  baseUrl,
  HEADERS_EXPORT_BEN,
  POST_CHECK_PRE_WCC,
  POST_LOAN_DB2_V2,
  POST_CANCEL_HAND,
  PUT_STATUS_SEND,
  HEADERS_EXPORT,
  GET_LAWYERS_LIST,
} from "../../API/apiUrls";
import axios from "axios";
import dayjs from "dayjs";
import ExportCancel from "./modal/ExportCancel";
import { ThaiRangePicker } from "../../../hook/ThaiDatePicker";
import { createPdfCancelHand } from "./report/ReportCancelHand";
import AddCancel from "./modal/AddCancel";
import { OPTIONS_TYPE } from "./constant/SelectOption";
import { CONTRACT_TYPES } from "./constant/CreateCancelHandOption";

// ============================================
// 🎯 MAIN COMPONENT - Create Cancel Hand Letter
// ============================================
const Main = () => {
  const { Option } = Select;

  // ==================== CONSTANTS ====================
  const userCompany = useMemo(() => localStorage.getItem("COMPANY_ID"), []);

  // ==================== STATE MANAGEMENT ====================
  const [arrayTable, setArrayTable] = useState([]);
  const [dataArr, setDataArr] = useState([]);
  const [dataRecord, setDataRecord] = useState();
  const [loading, setLoading] = useState(false);
  const [isModalExport, setIsModalExport] = useState(false);
  const [isModalAdd, setIsModalAdd] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [statusId, setStatusId] = useState();
  const [dateSearch, setDateSearch] = useState([]);
  const [searchInput, setSearchInput] = useState(null);
  const [dataType, setDataType] = useState();
  const [imageList, setImageList] = useState([]);
  const [imageLawyer, setImageLawyer] = useState(null);
  const [useLawyer, setUseLawyer] = useState(null);
  const [lawyerList, setLawyerList] = useState([]);
  const [advanceSearch, setAdvanceSearch] = useState(false);

  const mergedArrow = useMemo(() => true, []);

  // Refs
  const hasInitialized = useRef(false);

  // ==================== UTILITY FUNCTIONS ====================
  const convertUrlToBase64 = useCallback(async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  const formatName = useCallback((snam, name1, name2) => {
    return `${snam ?? ""} ${name1 ?? ""} ${name2 ?? ""}`
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  // ==================== LAWYER MANAGEMENT ====================
  const loadLawyerSignature = useCallback(
    async (lawyerId, lawyerData, currentImageList, showError = true) => {
      const expectedName = `lawyer/user/license_lawyer/public/${lawyerId}.png`;
      const lawyerLicense = currentImageList?.find(
        (item) => item.name === expectedName
      );

      if (!lawyerLicense) {
        if (showError) {
          notification.error({
            message: `ไม่พบไฟล์ลายเซ็น ${lawyerData.label}`,
            description: "กรุณาเลือกทนายคนอื่น !",
            duration: 5,
          });
        }
        setImageLawyer(null);
        return null;
      }

      const base64 = await convertUrlToBase64(lawyerLicense.url);
      const lawyerImage = { image: base64, lawyer: lawyerData };
      setImageLawyer(lawyerImage);
      return lawyerImage;
    },
    [convertUrlToBase64]
  );

  // ==================== DATA LOADING ====================
  const filterData = useCallback((data) => {
    if (!data) {
      console.error("data is not an array or is undefined");
      return;
    }

    const sortedData = [...data].sort((a, b) => {
      if (!a.docTime && b.docTime) return 1;
      if (a.docTime && !b.docTime) return -1;
      if (a.docTime && b.docTime) {
        return new Date(b.docTime) - new Date(a.docTime);
      }
      return 0;
    });

    const newData = sortedData.map((item, index) => ({
      ...item,
      key: index + 1,
    }));

    setArrayTable(newData);
    setDataArr(newData);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    const payload = { send: false };

    try {
      const res = await axios.post(POST_CHECK_PRE_WCC, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      if (res.status === 200) {
        setDataRecord(res.data);
        filterData(res.data);
        setAdvanceSearch(true);
      } else {
        message.warning("ไม่มีข้อมูล");
      }
    } catch (error) {
      console.error("Error loading data:", error);
      message.error(`ไม่พบข้อมูล: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [filterData]);

  // ==================== DATA PREPARATION ====================
  const mergeDataWithGuarantors = useCallback(
    (data = []) => {
      const rows = data.flatMap((record) => {
        const contno = record?.LOAN?.CONTNO ?? "";
        const dataType = record?.LOAN?.DATA_TYPE ?? "";
        const customer = record?.CUSTOMER ?? {};

        const customerAddresses = Array.isArray(customer.ADDRESS)
          ? customer.ADDRESS
          : customer.ADDRESS
          ? [customer.ADDRESS]
          : [];

        const mainCustomerRows = customerAddresses.map((address) => ({
          ...record,
          cusType: 0,
          CONTNO: contno,
          dataType,
          NAME: formatName(customer.SNAM, customer.NAME1, customer.NAME2),
          ADDRESS: address || {},
        }));

        let guarantorRows = [];
        if (dataType !== "lsfhp" && Array.isArray(record?.GUARANTORS)) {
          guarantorRows = record.GUARANTORS.map((guarantor) => {
            const addressList = Array.isArray(guarantor.ADDRESS)
              ? guarantor.ADDRESS
              : guarantor.ADDRESS
              ? [guarantor.ADDRESS]
              : [];

            const mainAddress =
              addressList.find(
                (addr) => String(addr?.ADDRNO) === String(guarantor?.ADDRMAIL)
              ) ||
              addressList[0] ||
              {};

            return {
              ...record,
              cusType: Number(guarantor?.GARNO) || 0,
              CONTNO: contno,
              dataType,
              NAME: formatName(
                guarantor.SNAM,
                guarantor.NAME1,
                guarantor.NAME2
              ),
              ADDRESS: mainAddress,
            };
          });
        }

        return [...mainCustomerRows, ...guarantorRows];
      });

      return rows.map((item, index) => ({ ...item, key: index + 1 }));
    },
    [formatName]
  );

  const handleExport = useCallback(
    async (rawData) => {
      setLoading(true);
      try {
        const processedData = mergeDataWithGuarantors(rawData);
        await createPdfCancelHand(processedData, imageLawyer);
        message.success("โหลด PDF สำเร็จ");
      } catch (error) {
        console.error(error);
        message.error(`พิมพ์ไม่สำเร็จ: ${error?.message || "Unknown error"}`);
      } finally {
        setLoading(false);
      }
    },
    [imageLawyer, mergeDataWithGuarantors]
  );

  const updateSendStatus = useCallback(
    async (rawData) => {
      const ids = [
        ...new Set(
          selectedRows.map((r) => Number(r.id)).filter(Number.isFinite)
        ),
      ];
      const payload = { id: ids };
      setLoading(true);

      try {
        const response = await axios.put(PUT_STATUS_SEND, payload, {
          headers: HEADERS_EXPORT_BEN,
        });

        const { status, data } = response;

        if (status === 200) {
          message.success("สร้างข้อมูลใหม่สำเร็จ");
          await loadData();
          await handleExport(rawData);
          setSelectedRowKeys([]);
          setSelectedRows([]);
        } else if (status === 201) {
          message.warning(String(data?.OK));
        } else {
          message.success(`สำเร็จ (รหัส ${status})`);
        }
      } catch (error) {
        console.error("updateSendStatus error:", error);
        message.error("เกิดข้อผิดพลาดในการอัพเดทสถานะ");
      } finally {
        setLoading(false);
      }
    },
    [selectedRows, loadData, handleExport]
  );

  const insertDataLoan = useCallback(
    async (rawData) => {
      if (!rawData || (Array.isArray(rawData) && rawData.length === 0)) {
        message.error("ไม่มีข้อมูลที่บันทึก");
        return;
      }

      setLoading(true);

      try {
        const response = await axios.post(POST_CANCEL_HAND, rawData, {
          headers: HEADERS_EXPORT_BEN,
        });

        const { status, data } = response;

        if (status === 200) {
          await updateSendStatus(rawData);
        } else if (status === 201) {
          message.warning(String(data?.OK));
        } else {
          message.success(`สำเร็จ (รหัส ${status})`);
        }
      } catch (error) {
        console.error("insertDataLoan error:", error);
        message.error("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
      } finally {
        setLoading(false);
      }
    },
    [updateSendStatus]
  );

  const queryMultiData = useCallback(
    async (payload) => {
      if (!payload || Object.keys(payload).length === 0) {
        message.error("ไม่มีข้อมูลสำหรับการค้นหา");
        return;
      }

      setLoading(true);

      try {
        const allContracts = [];

        CONTRACT_TYPES.forEach((type) => {
          if (payload[type]) {
            const contracts = payload[type]
              .split(",")
              .map((c) => c.trim().replace(/'/g, ""))
              .filter(Boolean);

            contracts.forEach((contno) => {
              allContracts.push({ type, contno });
            });
          }
        });

        if (allContracts.length === 0) {
          message.warning("ไม่มีเลขสัญญาที่ต้องค้นหา");
          setLoading(false);
          return;
        }

        const chunkSize = 20;
        const chunks = [];

        for (let i = 0; i < allContracts.length; i += chunkSize) {
          chunks.push(allContracts.slice(i, i + chunkSize));
        }

        const allResults = [];

        for (let i = 0; i < chunks.length; i++) {
          const chunk = chunks[i];

          const chunkPayload = CONTRACT_TYPES.reduce((acc, type) => {
            const contractsOfType = chunk
              .filter((item) => item.type === type)
              .map((item) => `'${item.contno}'`);

            acc[type] = contractsOfType.length ? contractsOfType.join(",") : "";
            return acc;
          }, {});
          console.log("chunkPayload", chunkPayload);

          try {
            const response = await axios.post(POST_LOAN_DB2_V2, chunkPayload, {
              headers: HEADERS_EXPORT_BEN,
            });

            if (response.status === 200 && response.data) {
              const chunkData = Array.isArray(response.data)
                ? response.data
                : [response.data];

              allResults.push(...chunkData);
            }
          } catch (chunkError) {
            console.error(`Chunk ${i + 1} failed:`, chunkError);
          }

          if (i < chunks.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }

        if (allResults.length === 0) {
          message.warning("ไม่พบข้อมูลสัญญาที่ค้นหา");
          return;
        }

        if (allResults.length < allContracts.length) {
          message.warning(
            `พบข้อมูล ${allResults.length} จาก ${allContracts.length} สัญญา`
          );
        }

        await insertDataLoan(allResults);
      } catch (error) {
        console.error("Error in queryMultiData:", error);
        const status = error?.response?.status;

        if (status === 404) {
          message.error("ไม่พบเลขที่สัญญา");
        } else {
          message.error(
            `เกิดข้อผิดพลาดในการดึงข้อมูล${status ? ` (${status})` : ""}`
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [insertDataLoan]
  );

  const handleDataQuery = useCallback(
    (rows = []) => {
      const buckets = CONTRACT_TYPES.reduce((acc, type) => {
        acc[type] = [];
        return acc;
      }, {});

      rows.forEach((row) => {
        const type = String(row?.dataType || "").toLowerCase();
        const contno = row?.contno?.trim();

        if (
          contno &&
          CONTRACT_TYPES.includes(type) &&
          !buckets[type].includes(contno)
        ) {
          buckets[type].push(contno);
        }
      });

      const payload = CONTRACT_TYPES.reduce((acc, type) => {
        acc[type] = buckets[type].length
          ? buckets[type].map((c) => `'${c}'`).join(",")
          : "";
        return acc;
      }, {});

      queryMultiData(payload);
    },
    [queryMultiData]
  );

  // ==================== EVENT HANDLERS ====================
  const handleDataExport = useCallback(() => {
    if (selectedRows.length > 0) {
      handleDataQuery(selectedRows);
    }
  }, [selectedRows, handleDataQuery]);

  const handlefilterData = useCallback(() => {
    setLoading(true);
    setSelectedRowKeys([]);
    setSelectedRows([]);

    let result = Array.isArray(dataArr) ? [...dataArr] : [];

    const query = (searchInput ?? "").trim().toLowerCase();
    if (query) {
      result = result.filter((item) =>
        String(item.contno ?? "")
          .toLowerCase()
          .includes(query)
      );
    }

    if (dataType) {
      result = result.filter((item) => {
        return (
          String(item.dataType ?? "").toLowerCase() ===
          String(dataType).toLowerCase()
        );
      });
    }

    if (typeof statusId === "boolean") {
      result = result.filter((item) => item.send === statusId);
    }

    if (Array.isArray(dateSearch) && dateSearch[0] && dateSearch[1]) {
      const startTimestamp = dayjs(dateSearch[0]).startOf("day").valueOf();
      const endTimestamp = dayjs(dateSearch[1]).endOf("day").valueOf();

      result = result.filter((item) => {
        if (!item.datePre) return false;
        const itemTimestamp = dayjs(item.datePre).valueOf();
        return itemTimestamp >= startTimestamp && itemTimestamp <= endTimestamp;
      });
    }

    setArrayTable(result);

    if (result.length === 0) {
      message.info("ไม่พบข้อมูลที่ตรงเงื่อนไข");
    }

    setLoading(false);
  }, [dataArr, searchInput, dataType, statusId, dateSearch]);

  const handleResetFilter = useCallback(() => {
    setStatusId(null);
    setDateSearch(null);
    setSearchInput(null);
    setDataType(null);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setArrayTable(dataArr);
  }, [dataArr]);

  const onSelectChange = useCallback((keys, rows) => {
    setSelectedRowKeys(keys);
    setSelectedRows(rows);
  }, []);

  const reloadData = useCallback(
    (shouldReload) => {
      if (shouldReload) {
        loadData();
      }
    },
    [loadData]
  );

  const renderStatus = useCallback((record) => {
    const color = record.send ? "green" : "red";
    const text = record.send ? "ออกจดหมายแล้ว" : "ยังไม่ส่งบอกเลิก";

    return (
      <Tag color={color} key={record.id} style={{ textAlign: "center" }}>
        {text}
      </Tag>
    );
  }, []);

  const onChangeSelectLawyer = useCallback(
    (value, option) => {
      loadLawyerSignature(value, option, imageList, true);
    },
    [loadLawyerSignature, imageList]
  );

  const toggle = useCallback(() => {
    setAdvanceSearch((v) => !v);
  }, []);

  // ==================== LIFECYCLE ====================
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;

      const initialize = async () => {
        setLoading(true);
        try {
          const [images, lawyerOptions] = await Promise.all([
            (async () => {
              const res = await axios.get(
                `${baseUrl}/files/lawyer/user/license_lawyer/public`
              );
              const imgs = Array.isArray(res.data) ? res.data : [];
              setImageList(imgs);
              return imgs;
            })(),
            (async () => {
              const res = await axios.get(`${baseUrl}${GET_LAWYERS_LIST}`, {
                headers: HEADERS_EXPORT,
              });

              const rawLawyers = Array.isArray(res.data) ? res.data : [];
              if (rawLawyers.length === 0) {
                message.error("ไม่มีข้อมูลลายเซ็นทนาย !");
                return [];
              }

              const filteredLawyers = rawLawyers.filter((lawyer) => {
                const isActive =
                  lawyer.ACTIVE_STATUS === 1 || lawyer.ACTIVE_STATUS === true;
                const isLawyer = lawyer.ROLE_ID === 3;
                const isCorrectCompany =
                  userCompany === "3"
                    ? lawyer.COMPANY_ID === 3
                    : lawyer.COMPANY_ID !== 3;

                return isActive && isLawyer && isCorrectCompany;
              });

              const options = filteredLawyers.map((lawyer) => ({
                value: lawyer.id,
                label: lawyer.NNAME,
                fname: lawyer.FNAME,
                lname: lawyer.LNAME,
              }));

              setLawyerList(options);
              return options;
            })(),
          ]);

          // Set default lawyer
          const defaultLawyer = lawyerOptions[0];
          if (defaultLawyer && images) {
            setUseLawyer(defaultLawyer.value);
            await loadLawyerSignature(
              defaultLawyer.value,
              defaultLawyer,
              images,
              false
            );
          }

          // Load data
          const payload = { send: false };
          const res = await axios.post(POST_CHECK_PRE_WCC, payload, {
            headers: HEADERS_EXPORT_BEN,
          });

          if (res.status === 200) {
            setDataRecord(res.data);

            // Filter data inline
            const data = res.data;
            if (data) {
              const sortedData = [...data].sort((a, b) => {
                if (!a.docTime && b.docTime) return 1;
                if (a.docTime && !b.docTime) return -1;
                if (a.docTime && b.docTime) {
                  return new Date(b.docTime) - new Date(a.docTime);
                }
                return 0;
              });

              const newData = sortedData.map((item, index) => ({
                ...item,
                key: index + 1,
              }));

              setArrayTable(newData);
              setDataArr(newData);
            }

            setAdvanceSearch(true);
          }
        } catch (error) {
          console.error("Initialization error:", error);
          message.error("เกิดข้อผิดพลาดในการโหลดข้อมูล");
        } finally {
          setLoading(false);
        }
      };

      initialize();
    }
  }, []); // Empty dependencies - run only once

  // ==================== TABLE CONFIGURATION ====================
  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: onSelectChange,
    }),
    [selectedRowKeys, onSelectChange]
  );

  const columns = useMemo(
    () => [
      {
        title: "ลำดับ",
        dataIndex: "key",
        key: "key",
        align: "center",
        width: "10%",
        render: (text, object) => object.key,
      },
      {
        title: "ประเภทบัญชี",
        dataIndex: "dataType",
        key: "dataType",
        align: "center",
        render: (_, record) => {
          const option = OPTIONS_TYPE.find(
            (o) => String(o.value) === String(record.dataType)
          );
          return <span>{option?.label ?? "-"}</span>;
        },
      },
      {
        title: "เลขสัญญา",
        dataIndex: "contno",
        key: "contno",
        align: "center",
        render: (text, record) => record.contno,
      },
      {
        title: "วันที่มอบงาน",
        dataIndex: "datePre",
        key: "datePre",
        align: "center",
        render: (text, record) => {
          return record.datePre
            ? dayjs(record.datePre).add(543, "year").format("D MMM YY")
            : null;
        },
      },
      {
        title: "รอบออกบอกเลิก",
        dataIndex: "dateSend",
        key: "dateSend",
        align: "center",
        render: (text, record) => {
          return record.dateSend
            ? dayjs(record.dateSend).add(543, "year").format("D MMM YY")
            : null;
        },
      },
      {
        title: "สถานะ",
        align: "center",
        render: (record) => renderStatus(record),
      },
    ],
    [renderStatus]
  );

  // ==================== RENDER ====================
  return (
    <>
      <Card>
        <Row>
          <Col span={18} style={{ textAlign: "left" }}>
            <Tooltip
              title={advanceSearch ? "ซ่อนตัวกรอง" : "แสดงตัวกรอง"}
              arrow={mergedArrow}
            >
              {!advanceSearch ? (
                <DownSquareOutlined
                  style={{ fontSize: 20, color: "blue", marginTop: 4 }}
                  onClick={toggle}
                />
              ) : (
                <UpSquareOutlined
                  style={{ fontSize: 20, color: "blue", marginTop: 4 }}
                  onClick={toggle}
                />
              )}
            </Tooltip>

            <Select
              placeholder="เลือกทนาย"
              showSearch
              optionFilterProp="label"
              value={useLawyer}
              onChange={(value, option) => {
                setUseLawyer(value);
                onChangeSelectLawyer(value, option);
              }}
              options={lawyerList}
              size="large"
              style={{
                width: "auto",
                maxWidth: 175,
                borderRadius: 6,
                marginRight: 10,
              }}
            />

            <Button
              color="blue"
              variant="outlined"
              icon={<ExportOutlined />}
              size="large"
              style={{
                padding: "0 24px",
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                marginRight: 10,
              }}
              disabled={
                !imageLawyer ||
                selectedRows.some((item) => item.send) ||
                selectedRows.length === 0
              }
              onClick={handleDataExport}
              loading={loading}
            >
              ออกจดหมายบอกเลิก
            </Button>

            <Button
              color="green"
              variant="outlined"
              icon={<PlusCircleOutlined />}
              size="large"
              style={{
                padding: "0 24px",
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                marginRight: 10,
              }}
              disabled={!imageLawyer}
              onClick={() => setIsModalAdd(true)}
              loading={loading}
            >
              เพิ่มข้อมูล
            </Button>
          </Col>
          <Col span={6} style={{ textAlign: "end" }}>
            <Button
              type="primary"
              danger
              ghost
              icon={<ImportOutlined />}
              size="small"
              onClick={() => setIsModalExport(true)}
              loading={loading}
            >
              นำเข้าเลขไปรษณีย์
            </Button>
          </Col>
        </Row>
      </Card>
      <Card>
        <Spin spinning={loading} size="large" tip="Loading...">
          <Row>
            {advanceSearch && (
              <Col
                span={24}
                style={{ textAlign: "start", marginBottom: "10px" }}
              >
                <Space size={10} wrap>
                  <Select
                    placeholder="เลือกสถานะ"
                    optionFilterProp="value"
                    onChange={(value) => setStatusId(value)}
                    value={statusId}
                    style={{ width: 200 }}
                    size="large"
                  >
                    <Option value="all">
                      <span style={{ marginRight: 8 }}>🗂️</span>
                      ทั้งหมด
                    </Option>
                    <Option value={false}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span role="img" aria-label="pending">
                          🕒
                        </span>
                        <span>รอรับงาน</span>
                      </div>
                    </Option>
                    <Option value={true}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span role="img" aria-label="found">
                          ✅
                        </span>
                        <span style={{ color: "green" }}>รับงานแล้ว</span>
                      </div>
                    </Option>
                  </Select>

                  <Select
                    placeholder="เลือกสัญญา"
                    options={OPTIONS_TYPE}
                    value={dataType}
                    onChange={(value) => setDataType(value)}
                    size="large"
                    style={{ width: "100%" }}
                    popupMatchSelectWidth={false}
                    allowClear
                  />

                  <ThaiRangePicker
                    value={dateSearch}
                    onChange={setDateSearch}
                    size="large"
                  />

                  <Input
                    placeholder="ค้นหาสัญญา"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    size="small"
                    suffix={<span style={{ marginRight: 5 }}>👤</span>}
                  />

                  <Button
                    type="default"
                    icon={<FilterOutlined />}
                    size="large"
                    style={{
                      borderRadius: 8,
                      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                      fontWeight: "bold",
                      backgroundColor: "#fff7d6",
                      color: "#333",
                      border: "1px solid #ffe58f",
                    }}
                    onClick={handlefilterData}
                  >
                    กรองข้อมูล
                  </Button>

                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleResetFilter}
                    style={{
                      color: "red",
                      borderRadius: 8,
                      fontWeight: "bold",
                      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    ล้าง
                  </Button>
                </Space>
              </Col>
            )}
            <Col span={24}>
              <Table
                size="small"
                columns={columns}
                dataSource={arrayTable}
                scroll={{ x: 850 }}
                rowSelection={rowSelection}
                footer={() => (
                  <div style={{ alignItems: "center" }}>
                    <p style={{ margin: 0 }}>
                      จำนวนที่เลือก {selectedRows.length ?? 0}
                      <br />
                      จำนวนสัญญาทั้งหมด {arrayTable?.length ?? 0}
                    </p>
                  </div>
                )}
              />
            </Col>
          </Row>
        </Spin>
      </Card>

      {isModalExport && (
        <ExportCancel open={isModalExport} close={setIsModalExport} />
      )}
      {isModalAdd && (
        <AddCancel
          open={isModalAdd}
          close={setIsModalAdd}
          dataDefault={dataRecord}
          func={reloadData}
        />
      )}
    </>
  );
};

const CreateCancelHand = MotionHoc(Main);
export default CreateCancelHand;
